import React, { useState, useEffect, useRef } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import axiosInstance from "../axiosinstance";
import Header from "./Header";
import "./Sidebar.css";
import { IoIosArrowBack, IoIosArrowForward } from "react-icons/io";
import { FiUsers } from "react-icons/fi"; // Importing the React icon for "System User"
import useKeyboardShortcuts from "./KeyBoardShortcuts"; // Import Hook
import { FaChevronUp, FaChevronDown } from "react-icons/fa6";
import { Tooltip } from "@mui/material";

const DEFAULT_MENU_ICON = "Dashboard";
const menuIconAliases = {
  setting: "settting",
  settings: "settting",
  gear: "settting",
  cog: "settting",
  dashboard: "Dashboard",
  clientmanagement: "Client management",
  ledgermanagement: "ledger management",
  invoicemanagement: "invoice management",
  recordpayment: "Record Payment",
  remindermanagement: "reminder management",
  visitormanagement: "visitor management",
  servicemaster: "Service Master",
  familygroup: "family-group",
  feecalculator: "fee-calculator",
  subsidiary: "subsidiary",
  companymanagement: "subsidiary",
  firms: "firms",
  deeds: "deeds",
  checklist: "checklist1",
  todaysdue: "due payment",
  duepayment: "due payment",
  caadmin: "admins",
  systemuser: "admins",
};

const getIconPath = (iconName) => {
  const rawIcon = (iconName || "").toString().trim();
  const baseName = rawIcon
    .replace(/\\/g, "/")
    .split("/")
    .pop()
    .replace(/\.svg$/i, "");
  const normalizedKey = baseName.toLowerCase().replace(/[\s_-]+/g, "");
  const mappedIcon = menuIconAliases[normalizedKey];
  const safeIcon = mappedIcon || baseName || DEFAULT_MENU_ICON;
  return `/assests/icons/${safeIcon}.svg`;
};

const underlineFirstLetter = (text) => {
  if (!text) return text;
  const firstLetter = text.charAt(0);
  const restOfText = text.slice(1);
  return (
    <>
      <u>{firstLetter}</u>
      {restOfText}
    </>
  );
};

function Sidebar({ onSidebarToggle }) {
  const [showSide, setShowSide] = useState(true);
  const [isExpanded, setIsExpanded] = useState(true);
  const [menuItems, setMenuItems] = useState([]);
  const [reportItems, setReportItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [showReports, setShowReports] = useState(false); // toggle dropdown
  const [openDropdowns, setOpenDropdowns] = useState({});
  const location = useLocation();
  const [activeModule, setActiveModule] = useState("");
  const navigate = useNavigate(); // Initialize navigation
  console.log("menu item", menuItems);
  const pathname = location.pathname || "";

  const isDeedsPath = (path) =>
    path.startsWith("/deeds") ||
    path.startsWith("/deed") ||
    path.startsWith("/deed-questions") ||
    path.startsWith("/deed_instance");

  const isChecklistPath = (path) => {
    const normalizedPath = (path || "").toLowerCase();
    return (
      normalizedPath === "/checklist" ||
      normalizedPath.startsWith("/checklist/") ||
      normalizedPath.startsWith("/checklist-") ||
      normalizedPath.startsWith("/edit-checklist/") ||
      normalizedPath === "/admin/checklist" ||
      normalizedPath.startsWith("/admin/checklist/")
    );
  };

  const isReminderHistoryPath = pathname
    .toLowerCase()
    .startsWith("/reminder-history");
  const reminderHistoryType = location.state?.historyType || "";

  const matchesRoute = (currentPath, route) => {
    const normalizedPath = (currentPath || "").toLowerCase();
    const normalizedRoute = (route || "").toLowerCase();

    return (
      normalizedPath === normalizedRoute ||
      normalizedPath.startsWith(`${normalizedRoute}/`)
    );
  };

  const isMenuActive = (item) => {
    if (!item) return false;

    const moduleName = item.module_name;

    if (isReminderHistoryPath) {
      if (moduleName === "Individual Clients") {
        return reminderHistoryType === "individual_clients";
      }

      if (moduleName === "Family Group") {
        return reminderHistoryType === "family_group";
      }
    }

    const routes = routeMatchers[moduleName];

    if (routes) {
      return routes.some((route) => matchesRoute(pathname, route));
    }

    return false;
  };

  // Toggle Sidebar
  const toggleSidebar = () => {
    setIsExpanded(!isExpanded);
    setShowSide(!showSide);
  };

  useEffect(() => {
    const keysPressed = new Set(); // Set to track pressed keys

    const handleKeyDown = (event) => {
      if (!event.key) return; // Ignore events where key is undefined

      const isMac = navigator.platform.toUpperCase().includes("MAC");
      const key = event.key.toLowerCase();

      keysPressed.add(key);

      if ((isMac && event.metaKey) || (!isMac && event.altKey)) {
        switch (key) {
          case "s":
            event.preventDefault();
            navigate("/service-master");
            break;
          case "c":
            event.preventDefault();
            navigate("/client-management");
            break;
          case "i":
            event.preventDefault();
            navigate("/invoice-management");
            break;
          case "l":
            event.preventDefault();
            navigate("/ledger-management");
            break;
          case "p":
            event.preventDefault();
            navigate("/record-payement");
            break;
          case "r":
            event.preventDefault();
            navigate("/reminder-management");
            break;
          case "v":
            event.preventDefault();
            navigate("/visitor");
            break;
          case "u":
            event.preventDefault();
            navigate("/system-users");
            break;
          case "e":
            event.preventDefault();
            navigate("/subsidiary-report");
            break;
        }
      }

      if (
        (isMac &&
          event.metaKey &&
          keysPressed.has("c") &&
          keysPressed.has("n")) ||
        (!isMac && event.altKey && keysPressed.has("c") && keysPressed.has("n"))
      ) {
        event.preventDefault();
        navigate("/client-management-form");
      }

      if (
        (isMac &&
          event.metaKey &&
          keysPressed.has("i") &&
          keysPressed.has("n")) ||
        (!isMac && event.altKey && keysPressed.has("i") && keysPressed.has("n"))
      ) {
        event.preventDefault();
        navigate("/Invoice-Form");
      }

      if (
        (isMac &&
          event.metaKey &&
          keysPressed.has("p") &&
          keysPressed.has("n")) ||
        (!isMac && event.altKey && keysPressed.has("p") && keysPressed.has("n"))
      ) {
        event.preventDefault();
        navigate("/record-payement-form");
      }

      if (
        (isMac &&
          event.metaKey &&
          keysPressed.has("e") &&
          keysPressed.has("n")) ||
        (!isMac && event.altKey && keysPressed.has("e"))
      ) {
        event.preventDefault();
        navigate("/subsidiary-report");
      }
    };

    const handleKeyUp = (event) => {
      if (!event.key) return;
      keysPressed.delete(event.key.toLowerCase());
    };

    window.addEventListener("keydown", handleKeyDown);
    window.addEventListener("keyup", handleKeyUp);

    return () => {
      window.removeEventListener("keydown", handleKeyDown);
      window.removeEventListener("keyup", handleKeyUp);
    };
  }, [navigate]);

  // Fetch menu items from API
  useEffect(() => {
    const fetchMenuItems = async () => {
      try {
        const response = await axiosInstance.get(`/api/fetch_menu_items`);

        console.log("API Response:", response.data);

        const filteredItems = response.data.module_name.filter(
          (item) => item.module_name !== "Password",
        );

        setMenuItems(filteredItems);
        setLoading(false);
      } catch (err) {
        console.error("Error fetching menu items:", err.message);
        setError(err.message);
        setLoading(false);
      }
    };

    fetchMenuItems();
  }, []);

  useEffect(() => {
    onSidebarToggle(isExpanded);
  }, [isExpanded, onSidebarToggle]);
  const routeMatchers = {
    Dashboard: ["/dashboard"],

    "Client Management": [
      "/client-management",
      "/client-management-form",
      "/edit-client-management",
      "/archived-clients",
    ],

    "Ledger Management": ["/ledger-management", "/view-ledger"],

    "Invoice Management": [
      "/invoice-management",
      "/invoice-form",
      "/edit-invoice",
      "/view-invoice",
      "/invoice-log",
      "/archived-invoice",
    ],

    "Record Payment": [
      "/record-payement",
      "/record-payement-form",
      "/edit-record-payement",
      "/payement-history",
    ],

    "System User": [
      "/system-users",
      "/users-form",
      "/edit-system-user",
      "/change-password",
      "/archived-users",
    ],

    "Company Management": [
      "/subsidary",
      "/new-subsidary",
      "/edit-subsidary",
      "/archived-subsidary",
    ],

    "Visitor Management": ["/visitor", "/visitor-form", "/Visitor-Form"],

    "Service Master": [
      "/service-master",
      "/service-master-form",
      "/edit-master-form",
    ],

    "Reminder Management": ["/reminder-history"],

    "Family Group": ["/family-group"],
    "Family Groups": [
      "/family-groups",
      "/family-group-form",
      "/edit-family-group",
    ],
    "Fee Calculator": ["/fee-calculator"],
    "Individual Clients": ["/individual-clients"],
    Checklist: [
      "/checklist",
      "/checklist-form",
      "/edit-checklist",
      "/checklist-view",
    ],

    "Subsidiary Report": ["/subsidiary-report"],
    "Invoice Report": ["/invoice-report"],
    "Payment Report": ["/payment-report"],
    Settings: ["/settings"],
    Firms: ["/super-admin", "/admin-form", "/edit-firms", "/archived-firms"],

    "CA admin": ["/ca-admin", "/ca-admin-form", "/edit-admin"],

    "Today's Due": ["/todays-due", "/all-due"],

    Deeds: ["/deeds"],
  };
  const moduleRoutes = {
    "Visitor Management": "visitor",
    Dashboard: "dashboard",
    "Company Management": "subsidary",
    "Ledger Management": "ledger-management",
    "Client Management": "client-management",
    "Invoice Management": "invoice-management",
    "Record Payment": "record-payement",
    "Reminder Management": "reminder-management",
    "Reminder Mgmnt": "reminder-management",
    "Service Master": "service-master",
    "System User": "system-users",
    Firms: "super-Admin",
    "CA admin": "ca-admin",
    "Subsidiary Report": "subsidiary-report",
    "Invoice Report": "invoice-report",
    "Payment Report": "payment-report",
    Settings: "settings",
    Checklist: "checklist",
    "Family Group": "family-group",
    "Family Groups": "family-groups",
    "Individual Clients": "individual-clients",
    "Fee Calculator": "Fee-Calculator",
    "Family Group Reminder": "family-group",
    "Family Group Reminder Management": "family-group",
    Reports: "reports",
  };

  const getMenuRoute = (item) => {
    const moduleName = (item?.module_name || "").toString().trim();
    const mappedRoute = moduleRoutes[moduleName];
    const fallbackRoute = moduleName.replace(/\s+/g, "-").toLowerCase();
    const candidateRoute = (mappedRoute || fallbackRoute || "dashboard")
      .toString()
      .trim()
      .replace(/^\/+/, "");

    // Guard against accidental icon/asset strings being used as routes.
    if (
      candidateRoute.includes("assests/icons") ||
      candidateRoute.toLowerCase().endsWith(".svg")
    ) {
      return "dashboard";
    }

    return candidateRoute;
  };

  useEffect(() => {
    const currentPath = location.pathname.replace("/", "");
    const matchingModule = Object.keys(moduleRoutes).find(
      (key) => moduleRoutes[key] === currentPath,
    );
    console.log("Current Path:", currentPath);
    console.log("Matching Module:", matchingModule);
    setActiveModule(matchingModule || "");
  }, [location.pathname]);

  useEffect(() => {
    const nextOpenDropdowns = {};

    menuItems
      .filter((item) => item.parent_id === null || item.parent_id === "null")
      .forEach((parentItem) => {
        const children = menuItems.filter(
          (child) => child.parent_id === parentItem.menu_id,
        );

        if (
          children.length > 0 &&
          (isMenuActive(parentItem) || children.some((child) => isMenuActive(child)))
        ) {
          nextOpenDropdowns[parentItem.menu_id] = true;
        }
      });

    setOpenDropdowns((prev) => ({
      ...prev,
      ...nextOpenDropdowns,
    }));
  }, [location.pathname, location.state, menuItems]);

  const toggleDropdown = (menuId) => {
    setOpenDropdowns((prev) => ({
      ...prev,
      [menuId]: !prev[menuId],
    }));
  };

  const handleIconError = (event) => {
    if (!event?.currentTarget) return;
    event.currentTarget.onerror = null;
    event.currentTarget.src = getIconPath(DEFAULT_MENU_ICON);
  };

  return (
    <>
      <div
        className={`sidebar-container ${
          isExpanded ? "office-sidebar-expanded" : "office-sidebar-collapsed"
        }`}
      >
        {showSide && (
          <div className="sidebar-container-content1">
            <div className="sidebar-header">
              <div className="sidebar-brand">
                {/* <img
                  src="/Office_logo_final.png"
                  alt="Office Align"
                  className="sidebar-logo"
                /> */}
                <img
                  src="/Office_align.png"
                  alt="Office Align"
                  className="sidebar-logo"
                />
                {/* <span className="sidebar-brand-name">Office Align</span> */}
              </div>
            </div>

            <div className="menu-parent">
              <div className="menu-items">
                {loading ? (
                  <p>Loading menu...</p>
                ) : error ? (
                  <p>Error: {error}</p>
                ) : (
                  <>
                    {menuItems
                      .filter(
                        (item) =>
                          item.parent_id === null || item.parent_id === "null",
                      ) // Get parent items
                      .map((parentItem) => {
                        const children = menuItems.filter(
                          (child) => child.parent_id === parentItem.menu_id,
                        );

                        const route = getMenuRoute(parentItem);

                        const iconPath = getIconPath(parentItem.icons);

                        return (
                          <div
                            key={parentItem.menu_id}
                            className="sidebar-item-wrapper"
                          >
                            {children.length > 0 ? (
                              <>
                                {/* Parent with dropdown */}
                                <div
                                  className={`sidebar-item ${
                                    isMenuActive(parentItem) ? "active" : ""
                                  }`}
                                  onClick={() =>
                                    toggleDropdown(parentItem.menu_id)
                                  }
                                  style={{ cursor: "pointer" }}
                                >
                                  <div className="icon">
                                    {parentItem.module_name ===
                                    "System User" ? (
                                      <FiUsers size={20} />
                                    ) : (
                                      <img
                                        src={iconPath}
                                        alt={parentItem.module_name}
                                        className="sidebar-icon"
                                        onError={handleIconError}
                                      />
                                    )}
                                  </div>
                                  <span className="menu-btn jost-regular">
                                    {underlineFirstLetter(
                                      parentItem.module_name,
                                    )}
                                  </span>
                                  <span className="arrow-icon">
                                    {openDropdowns[parentItem.menu_id] ? (
                                      <FaChevronUp />
                                    ) : (
                                      <FaChevronDown />
                                    )}
                                  </span>
                                </div>

                                {openDropdowns[parentItem.menu_id] && (
                                  <div className="submenu">
                                    <ol className="submenu-list">
                                      {children.map((child, index) => {
                                        const childRoute = getMenuRoute(child);
                                        return (
                                          <li
                                            key={child.menu_id}
                                            className="submenu-list-item"
                                          >
                                            <Link
                                              to={`/${childRoute}`}
                                              className={`sidebar-item sub-item ${
                                                isMenuActive(child)
                                                  ? "active"
                                                  : ""
                                              }`}
                                            >
                                              <span className="submenu-text">
                                                {child.module_name}
                                              </span>
                                            </Link>
                                          </li>
                                        );
                                      })}
                                    </ol>
                                  </div>
                                )}
                              </>
                            ) : (
                              /* Normal single item */
                              <Link
                                to={`/${route}`}
                                className={`sidebar-item ${
                                  isMenuActive(parentItem) ? "active" : ""
                                }`}
                              >
                                <div className="icon">
                                  {parentItem.module_name === "System User" ? (
                                    <FiUsers size={20} />
                                  ) : (
                                    <img
                                      src={iconPath}
                                      alt={parentItem.module_name}
                                      className="sidebar-icon"
                                      onError={handleIconError}
                                    />
                                  )}
                                </div>
                                <span className="menu-btn jost-regular">
                                  {underlineFirstLetter(parentItem.module_name)}
                                </span>
                              </Link>
                            )}
                          </div>
                        );
                      })}
                  </>
                )}
              </div>
            </div>
          </div>
        )}

        {/* Collapsed Sidebar */}
        {!showSide && (
          <div className="sidebar-container-content2">
            <div className="sidebar-header sidebar-header-2">
              <img
                src="/assests/logo/favicon.ico"
                alt="Office Align"
                className="sidebar-logo"
              />
            </div>
            <div className="menu-items">
              {menuItems
                .filter(
                  (item) =>
                    item.parent_id === null || item.parent_id === "null",
                )
                .map((parent) => {
                  const iconPath = getIconPath(parent.icons);
                  const route = getMenuRoute(parent);

                  return (
                    <Tooltip title={parent.module_name} placement="right" arrow>
                      <Link
                        key={parent.menu_id}
                        to={`/${route}`}
                        className={`sidebar-item-wrapper ${
                          isMenuActive(parent) ? "active" : ""
                        }`}
                      >
                        <span className="icon">
                          {parent.module_name === "System User" ? (
                            <FiUsers size={20} />
                          ) : (
                            <img
                              src={iconPath}
                              alt={parent.module_name}
                              className="sidebar-icon"
                              onError={handleIconError}
                            />
                          )}
                        </span>
                      </Link>
                    </Tooltip>
                  );
                })}
            </div>
          </div>
        )}

        {/* Footer Expand/Collapse */}
        <div className="sidebar-footer" onClick={toggleSidebar}>
          <div className="expand-sidebar">
            {isExpanded ? <IoIosArrowBack /> : <IoIosArrowForward />}
          </div>
        </div>
      </div>
    </>
  );
}

export default Sidebar;
